import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
  FieldValue,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from '@/lib/firebase';
import { httpsCallable, functions } from '@/lib/functions';
import { yieldIosOutboundFirebaseWork } from '@/utils/iosFirebaseNetworkYield';
import { notifyLiveTripDeleted, type CollaboratorPermission, type Collaborator } from './trips';
import { logAnalyticsEvent } from './analyticsLogger';
import { isTripLimitEntitlementError } from './entitlements';
// NOTE: Collaboration push notifications are handled by Cloud Function `onCollaboratorAdded`
// which sends FCM notifications to the invitee's device (not local notifications)
import { logger } from '@/services/logger';

export type InviteLink = {
  id: string;
  tripId: string;
  createdBy: string;
  createdAt: Date;
  expiresAt?: Date;
  permission: CollaboratorPermission;
  maxUses?: number;
  usedCount: number;
  isActive: boolean;
};

export type FirestoreInviteLink = {
  tripId: string;
  createdBy: string;
  createdAt: Timestamp | string | FieldValue;
  expiresAt?: Timestamp | string;
  permission: CollaboratorPermission;
  maxUses?: number;
  usedCount: number;
  isActive: boolean;
};

const addCollaboratorWithEntitlementCallable = httpsCallable<
  { tripId: string; email: string; permission: CollaboratorPermission; invitedByName?: string },
  {
    success: boolean;
    isPending: boolean;
    collaborator?: {
      userId: string;
      email: string;
      name?: string;
      permission: CollaboratorPermission;
      addedBy: string;
      addedAtIso: string;
    };
  }
>(functions, 'addCollaboratorWithEntitlement');

const acceptInviteWithEntitlementCallable = httpsCallable<
  { linkId: string; userName?: string },
  { success: boolean; tripId: string }
>(functions, 'acceptInviteWithEntitlement');

const leaveTripAsCollaboratorCallable = httpsCallable<{ tripId: string }, { success: boolean }>(
  functions,
  'leaveTripAsCollaborator',
);

const updateCollaboratorPermissionCallable = httpsCallable<
  { tripId: string; userId: string; permission: CollaboratorPermission },
  { success: boolean }
>(functions, 'updateCollaboratorPermission');

const removeCollaboratorFromTripCallable = httpsCallable<
  { tripId: string; userId: string },
  { success: boolean }
>(functions, 'removeCollaboratorFromTrip');

const syncTripMembershipCallable = httpsCallable<{ tripId: string }, { success: boolean }>(
  functions,
  'syncTripMembership',
);

const FREE_PLAN_ACTIVE_TRIP_COLLABORATION_MESSAGE =
  "This user is on the Free plan and already has an active trip, so they can't be added to another active trip. Ask them to archive or leave one active trip first, or upgrade to Premium.";

async function withIosCallableYield<T>(run: () => Promise<T>): Promise<T> {
  await yieldIosOutboundFirebaseWork();
  return run();
}

function parseCallableClientError(error: unknown): { code: string; message: string } {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: typeof error.message === 'string' ? error.message : '' };
  }
  const e = error as { code?: unknown; message?: unknown } | null;
  const code = typeof e?.code === 'string' ? e.code : '';
  const message =
    typeof e?.message === 'string'
      ? e.message
      : error instanceof Error
        ? error.message
        : '';
  return { code, message };
}

// Get user profile by email (via callable – Firestore rules allow users to read only their own doc)
export const getUserByEmail = async (
  email: string,
  tripId: string,
): Promise<{ uid: string; name?: string; email: string } | null> => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const lookupUserByEmail = httpsCallable<
      { email: string; tripId: string },
      { uid: string; name?: string; email: string } | null
    >(
      functions,
      'lookupUserByEmail'
    );
    const { data } = await lookupUserByEmail({ email: normalizedEmail, tripId });
    if (data) {
      logger.debug('Found user by lookupUserByEmail', { userId: data.uid, email: data.email });
      return data;
    }
    logger.warn('User not found', {
      email: normalizedEmail,
      possibleReasons: [
        'User document missing email field',
        'Email mismatch (case/format)',
        'User not completed onboarding',
        'User does not exist in Firestore',
      ],
    });
    return null;
  } catch (error) {
    logger.error('Error fetching user by email', error, { email: email?.toLowerCase?.()?.trim() });
    return null;
  }
};

// Create pending invitation for users not yet registered
export const createPendingInvitation = async (
  tripId: string,
  email: string,
  permission: CollaboratorPermission,
  invitedBy: string,
  invitedByName?: string
): Promise<{ success: boolean; error?: string; invitationId?: string }> => {
  const normalizedEmail = email.toLowerCase().trim();
  try {
    // Check if pending invitation already exists.
    // Include invitedBy so rules can prove every matching doc is readable
    // (read allows invitedBy == auth.uid OR invitee email match).
    try {
      const pendingQuery = query(
        collection(db, 'pendingInvitations'),
        where('tripId', '==', tripId),
        where('email', '==', normalizedEmail),
        where('status', '==', 'pending'),
        where('invitedBy', '==', invitedBy)
      );

      const existingSnapshot = await getDocs(pendingQuery);
      if (!existingSnapshot.empty) {
        return { success: false, error: 'An invitation has already been sent to this email address' };
      }
    } catch (queryError) {
      // Some older rule/data combinations can reject this read due to missing legacy fields.
      // Continue and rely on write-side checks so "add collaborator" still succeeds.
      logger.warn('Pending invitation pre-check failed; proceeding with create', {
        tripId,
        invitedBy,
        email: normalizedEmail,
        error: queryError instanceof Error ? queryError.message : String(queryError),
      });
    }
    
    // Create pending invitation
    const invitationRef = doc(collection(db, 'pendingInvitations'));
    const invitationId = invitationRef.id;
    
    // Build invitation data, only including defined fields
    const invitationData: any = {
      tripId,
      email: normalizedEmail,
      normalizedEmail,
      permission,
      invitedBy,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: null, // No expiration for now
    };
    
    // Only add invitedByName if it's provided
    if (invitedByName) {
      invitationData.invitedByName = invitedByName;
    }
    
    await setDoc(invitationRef, invitationData);

    void logAnalyticsEvent('invite_sent', {
      tripId,
      actorId: invitedBy,
      source: 'pending_invitation',
      result: 'success',
      isPending: true,
    });
    
    return { success: true, invitationId };
  } catch (error) {
    logger.error('Error creating pending invitation', error, { tripId, email: normalizedEmail, permission });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to create pending invitation' 
    };
  }
};

// Add collaborator to trip by email
export const addCollaboratorByEmail = async (
  tripId: string,
  email: string,
  permission: CollaboratorPermission,
  addedBy: string,
  addedByName?: string
): Promise<{ success: boolean; error?: string; collaborator?: Collaborator; isPending?: boolean }> => {
  try {
    const normalizedEmail = email.toLowerCase().trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail)) {
      return { success: false, error: 'Please enter a valid email address.' };
    }

    const { data } = await withIosCallableYield(() =>
      addCollaboratorWithEntitlementCallable({
      tripId,
      email: normalizedEmail,
      permission,
      invitedByName: addedByName,
      }),
    );
    if (!data.success) {
      return { success: false, error: 'Failed to add collaborator' };
    }
    if (data.isPending) {
      return {
        success: true,
        isPending: true,
        error: undefined,
      };
    }
    const rawCollaborator = data.collaborator;
    if (!rawCollaborator) {
      return { success: false, error: 'Failed to add collaborator' };
    }
    const appCollaborator: Collaborator = {
      userId: rawCollaborator.userId,
      email: rawCollaborator.email,
      name: rawCollaborator.name,
      permission: rawCollaborator.permission,
      addedAt: new Date(rawCollaborator.addedAtIso),
      addedBy: rawCollaborator.addedBy,
    };
    
    // NOTE: Push notifications to the new collaborator are handled by the Cloud Function
    // `onCollaboratorAdded` which sends FCM notifications to the invitee's device.
    // We do NOT send a local notification here because:
    // 1. Local notifications can only go to the current device (inviter's device)
    // 2. The message "You've been added..." would confuse the inviter
    // The new collaborator will receive an FCM push notification and the trip will appear in their trips list.
    void logAnalyticsEvent('invite_sent', {
      tripId,
      actorId: addedBy,
      source: 'collaborator_add',
      result: 'success',
      isPending: false,
    });
    void logAnalyticsEvent('collaborator_added', {
      tripId,
      collaboratorId: appCollaborator.userId,
      collaboratorEmail: appCollaborator.email,
    });
    void syncTripMembershipCallable({ tripId }).catch((syncError) => {
      console.warn('[addCollaboratorByEmail] Membership sync after collaborator add failed', syncError);
    });
    
    return { success: true, collaborator: appCollaborator, isPending: false };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[addCollaboratorByEmail] Error', { message });
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof (error as { message?: unknown } | null)?.message === 'string'
          ? String((error as { message: string }).message)
          : 'Failed to add collaborator';
    
    if (isTripLimitEntitlementError(error)) {
      return { success: false, error: FREE_PLAN_ACTIVE_TRIP_COLLABORATION_MESSAGE };
    }
    if (errorMessage.includes('already') || errorMessage.includes('exists')) {
      return { success: false, error: 'This user is already a collaborator on this trip.' };
    }
    if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
      return { success: false, error: 'Only the trip owner can add collaborators.' };
    }
    if (errorMessage.includes('network') || errorMessage.includes('Network')) {
      return { success: false, error: 'Network error. Please check your internet connection and try again.' };
    }
    
    return { success: false, error: errorMessage };
  }
};

// Update collaborator permission
export const updateCollaboratorPermission = async (
  tripId: string,
  userId: string,
  permission: CollaboratorPermission,
  updatedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data } = await withIosCallableYield(() =>
      updateCollaboratorPermissionCallable({ tripId, userId, permission }),
    );
    if (!data?.success) {
      return { success: false, error: 'Failed to update permission' };
    }
    return { success: true };
  } catch (error) {
    console.error('Error updating collaborator permission:', error);
    const { code, message } = parseCallableClientError(error);
    if (code === 'functions/not-found' || message.toLowerCase().includes('not found')) {
      return { success: false, error: 'Collaborator not found' };
    }
    if (code === 'functions/permission-denied' || message.toLowerCase().includes('permission')) {
      return { success: false, error: 'Only trip owner can update permissions' };
    }
    return { success: false, error: message || 'Failed to update permission' };
  }
};

// Remove collaborator
export const removeCollaborator = async (
  tripId: string,
  userId: string,
  removedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('[removeCollaborator] Starting removal');
    const { data } = await withIosCallableYield(() =>
      removeCollaboratorFromTripCallable({ tripId, userId }),
    );
    if (!data?.success) {
      return { success: false, error: 'Failed to remove collaborator' };
    }
    console.log('[removeCollaborator] Successfully removed collaborator');
    return { success: true };
  } catch (error) {
    console.error('[removeCollaborator] Error:', error);
    const { code, message } = parseCallableClientError(error);
    const errorMessage = message || 'Failed to remove collaborator';

    // Provide more specific error messages
    if (code === 'functions/not-found' || errorMessage.toLowerCase().includes('not found')) {
      return { success: false, error: 'Collaborator not found in this trip.' };
    }
    if (code === 'functions/permission-denied' || errorMessage.includes('permission') || errorMessage.includes('Permission')) {
      return { success: false, error: 'Only the trip owner can remove collaborators.' };
    }
    if (errorMessage.includes('network') || errorMessage.includes('Network')) {
      return { success: false, error: 'Network error. Please check your internet connection and try again.' };
    }
    
    return { success: false, error: errorMessage };
  }
};

// Leave trip (collaborator removes themselves; owner cannot leave)
export const leaveTrip = async (
  tripId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data } = await withIosCallableYield(() => leaveTripAsCollaboratorCallable({ tripId }));
    if (!data?.success) {
      return { success: false, error: 'Failed to leave trip. Please try again.' };
    }
    notifyLiveTripDeleted(userId, tripId);
    return { success: true };
  } catch (error: unknown) {
    console.error('[leaveTrip] Error:', error);
    const { code, message } = parseCallableClientError(error);
    const lower = message.toLowerCase();

    if (code === 'functions/not-found') {
      if (lower.includes('trip not found')) {
        return { success: false, error: 'Trip not found. Please refresh and try again.' };
      }
      return {
        success: false,
        error:
          'Could not reach the leave-trip service. Deploy `leaveTripAsCollaborator` to Firebase (us-central1), then try again.',
      };
    }
    if (
      code === 'functions/deadline-exceeded' ||
      lower.includes('deadline') ||
      lower.includes('timeout')
    ) {
      return { success: false, error: 'Request timed out. Check your connection and try again.' };
    }
    if (code === 'functions/permission-denied' || lower.includes('permission')) {
      if (message.includes('owners cannot leave')) {
        return {
          success: false,
          error: 'Trip owners cannot leave. Transfer ownership first or delete the trip.',
        };
      }
      if (message.includes('not a collaborator')) {
        return { success: false, error: 'You are not a collaborator on this trip.' };
      }
      return { success: false, error: message || 'You do not have permission to leave this trip.' };
    }
    if (code === 'functions/resource-exhausted') {
      return { success: false, error: 'Too many attempts. Please try again in a few minutes.' };
    }
    if (
      code === 'functions/unavailable' ||
      lower.includes('network') ||
      lower.includes('fetch') ||
      lower.includes('network request failed')
    ) {
      return { success: false, error: 'Network error. Please check your connection and try again.' };
    }
    if (code === 'functions/internal' && message.trim().length === 0) {
      return {
        success: false,
        error:
          'Could not leave this trip (server error). Confirm `leaveTripAsCollaborator` is deployed to Firebase, then try again.',
      };
    }
    if (message.trim().length > 0) {
      return { success: false, error: message };
    }
    return {
      success: false,
      error:
        'Could not leave this trip. If this keeps happening, confirm Firebase `leaveTripAsCollaborator` is deployed and you are online.',
    };
  }
};

// Generate invite link
export const generateInviteLink = async (
  tripId: string,
  permission: CollaboratorPermission,
  createdBy: string,
  expiresInDays?: number,
  maxUses?: number
): Promise<{ success: boolean; error?: string; inviteLink?: InviteLink; linkId?: string }> => {
  try {
    // Ownership is enforced in Firestore rules (trip owner only); avoids an extra trip read before write.
    // Generate a unique link ID
    const inviteLinkRef = doc(collection(db, 'tripInvites'));
    const linkId = inviteLinkRef.id;
    
    const expiresAt = expiresInDays
      ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000)
      : undefined;
    
    const inviteLink: FirestoreInviteLink = {
      tripId,
      createdBy,
      createdAt: serverTimestamp(),
      expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : undefined,
      permission,
      usedCount: 0,
      isActive: true,
      ...(typeof maxUses === 'number' ? { maxUses } : {}),
    };
    
    await setDoc(inviteLinkRef, inviteLink);
    
    const appInviteLink: InviteLink = {
      id: linkId,
      tripId,
      createdBy,
      createdAt: new Date(),
      expiresAt,
      permission,
      maxUses,
      usedCount: 0,
      isActive: true,
    };
    
    return { success: true, inviteLink: appInviteLink, linkId };
  } catch (error) {
    console.error('Error generating invite link:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to generate invite link' };
  }
};

// Get invite link details
export const getInviteLink = async (linkId: string): Promise<InviteLink | null> => {
  try {
    const inviteLinkRef = doc(db, 'tripInvites', linkId);
    const inviteLinkDoc = await getDoc(inviteLinkRef);
    
    if (!inviteLinkDoc.exists()) {
      return null;
    }
    
    const data = inviteLinkDoc.data() as FirestoreInviteLink;
    
    return {
      id: linkId,
      tripId: data.tripId,
      createdBy: data.createdBy,
      createdAt: data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate() 
        : typeof data.createdAt === 'string'
        ? new Date(data.createdAt)
        : new Date(), // Fallback if FieldValue (shouldn't happen when reading)
      expiresAt: data.expiresAt
        ? data.expiresAt instanceof Timestamp
          ? data.expiresAt.toDate()
          : typeof data.expiresAt === 'string'
          ? new Date(data.expiresAt)
          : undefined
        : undefined,
      permission: data.permission,
      maxUses: data.maxUses,
      usedCount: data.usedCount,
      isActive: data.isActive,
    };
  } catch (error) {
    console.error('Error fetching invite link:', error);
    return null;
  }
};

// Accept invite link
export const acceptInviteLink = async (
  linkId: string,
  _userId: string,
  _userEmail: string,
  userName?: string
): Promise<{ success: boolean; error?: string; tripId?: string }> => {
  try {
    const trimmedName = userName?.trim();
    const { data } = await withIosCallableYield(() =>
      acceptInviteWithEntitlementCallable({
      linkId,
      userName: trimmedName && trimmedName.length > 0 ? trimmedName : undefined,
      }),
    );
    if (data.success && data.tripId) {
      void logAnalyticsEvent('invite_accepted', {
        tripId: data.tripId,
        actorId: _userId,
        source: 'invite_link',
        result: 'success',
        linkId,
      });
      void logAnalyticsEvent('collaborator_joined', {
        tripId: data.tripId,
        collaboratorId: _userId,
        source: 'invite_link',
      });
    }
    return { success: data.success, tripId: data.tripId };
  } catch (error) {
    console.error('Error accepting invite link:', error);
    if (isTripLimitEntitlementError(error)) {
      return { success: false, error: FREE_PLAN_ACTIVE_TRIP_COLLABORATION_MESSAGE };
    }
    return { success: false, error: error instanceof Error ? error.message : 'Failed to accept invite' };
  }
};

// Deactivate invite link
export const deactivateInviteLink = async (
  linkId: string,
  deactivatedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const inviteLinkRef = doc(db, 'tripInvites', linkId);
    const inviteLinkDoc = await getDoc(inviteLinkRef);
    
    if (!inviteLinkDoc.exists()) {
      return { success: false, error: 'Invite link not found' };
    }
    
    const inviteLink = inviteLinkDoc.data() as FirestoreInviteLink;
    
    // Verify user is the trip owner
    const tripRef = doc(db, 'trips', inviteLink.tripId);
    const tripDoc = await getDoc(tripRef);
    
    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }
    
    const tripData = tripDoc.data();
    if (tripData.owner !== deactivatedBy) {
      return { success: false, error: 'Only trip owner can deactivate invite links' };
    }
    
    await updateDoc(inviteLinkRef, {
      isActive: false,
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error deactivating invite link:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Failed to deactivate invite link' };
  }
};

// Get user's permission for a trip
export const getUserTripPermission = async (
  tripId: string,
  userId: string
): Promise<CollaboratorPermission | 'owner' | null> => {
  try {
    const tripRef = doc(db, 'trips', tripId);
    const tripDoc = await getDoc(tripRef);
    
    if (!tripDoc.exists()) {
      return null;
    }
    
    const tripData = tripDoc.data();
    
    if (tripData.owner === userId) {
      return 'owner';
    }
    
    const collaborators = tripData.collaborators || [];
    const collaborator = collaborators.find((c: any) => c.userId === userId);
    
    return collaborator ? collaborator.permission : null;
  } catch (error) {
    console.error('Error getting user permission:', error);
    return null;
  }
};

// Helper function to check if user can perform action
export const canUserEditTrip = (permission: CollaboratorPermission | 'owner' | null): boolean => {
  return permission === 'owner' || permission === 'edit';
};

export const canUserAddItems = (permission: CollaboratorPermission | 'owner' | null): boolean => {
  return permission === 'owner' || permission === 'edit' || permission === 'add_items';
};

/**
 * Check if a user can edit a specific item (expense, booking, itinerary item, etc.)
 * @param userId - The user ID to check
 * @param itemCreatedBy - The user ID who created the item
 * @param permission - The user's permission level for the trip
 * @param isOwner - Whether the user is the trip owner
 * @returns true if the user can edit the item
 */
export const canUserEditItem = (
  userId: string,
  itemCreatedBy: string,
  permission: CollaboratorPermission | 'owner' | null,
  isOwner: boolean
): boolean => {
  // Owner can edit everything
  if (isOwner) return true;
  
  // Users with 'edit' permission can edit everything
  if (permission === 'edit') return true;
  
  // Users with 'add_items' permission can only edit their own items
  if (permission === 'add_items' && itemCreatedBy === userId) return true;
  
  return false;
};

// Type for Firestore collaborator
type FirestoreCollaborator = {
  userId: string;
  email: string;
  normalizedEmail?: string;
  name?: string;
  permission: CollaboratorPermission;
  addedAt: Timestamp | ReturnType<typeof serverTimestamp>;
  addedBy: string;
};

